// Vendor API client for the Ludic Drive Assetstore backend.
// All endpoints under /api/vendor/* require an approved vendor token.

import { API_URL } from '../config';
import { getAccessToken } from './token';

const API_BASE_URL = API_URL;

class ApiError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string> | undefined),
  };

  const token = getAccessToken() || undefined;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new ApiError(
        response.statusText || 'Request failed',
        response.status,
        (data as { detail?: string })?.detail || (data as { message?: string })?.message
      );
      throw error;
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Network error', undefined, String(error));
  }
}

function queryString(params?: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        search.append(key, String(value));
      }
    });
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  base: API_BASE_URL,

  // Vendor status / application
  apply: {
    apply: () => request<{ status: string }>('/api/vendor/apply', { method: 'POST' }),
  },

  // Asset management
  assets: {
    list: (params?: {
      search?: string;
      category?: string;
      status_filter?: string;
      limit?: number;
      offset?: number;
    }) =>
      request<{ assets: VendorAsset[]; total: number }>(`/api/vendor/assets${queryString(params)}`),
    get: (id: number) => request<VendorAsset>(`/api/vendor/assets/${id}`),
    create: (payload: AssetPayload) =>
      request<VendorAsset>('/api/vendor/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    update: (id: number, payload: Partial<AssetPayload>) =>
      request<VendorAsset>(`/api/vendor/assets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    delete: (id: number) => request<void>(`/api/vendor/assets/${id}`, { method: 'DELETE' }),
    hide: (id: number) => request<VendorAsset>(`/api/vendor/assets/${id}/hide`, { method: 'POST' }),
    unhide: (id: number) =>
      request<VendorAsset>(`/api/vendor/assets/${id}/unhide`, { method: 'POST' }),
    submit: (id: number) =>
      request<VendorAsset>(`/api/vendor/assets/${id}/submit`, { method: 'POST' }),
    addVersion: (id: number, payload: VersionPayload) =>
      request<VendorVersion>(`/api/vendor/assets/${id}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    updateVersion: (id: number, versionId: number, payload: Partial<VersionPayload>) =>
      request<VendorVersion>(`/api/vendor/assets/${id}/versions/${versionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    deleteVersion: (id: number, versionId: number) =>
      request<void>(`/api/vendor/assets/${id}/versions/${versionId}`, { method: 'DELETE' }),
  },

  // Public catalogue metadata
  categories: {
    list: () => request<string[]>('/api/products/categories'),
  },

  // Asset package / thumbnail upload
  upload: {
    file: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request<UploadResult>('/api/vendor/upload', { method: 'POST', body: form });
    },
    presign: (payload: { filename: string; file_size: number; checksum: string }) =>
      request<UploadResult>('/api/vendor/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  },

  // Sales & payouts
  sales: {
    list: (params?: { limit?: number; offset?: number }) =>
      request<SalesResponse>(`/api/vendor/sales${queryString(params)}`),
  },
  payouts: {
    list: () => request<VendorPayout[]>('/api/vendor/payouts'),
    request: (payload: { amount: number; method: string; note?: string }) =>
      request<VendorPayout>('/api/vendor/payouts/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
  },

  // Dashboard
  stats: {
    get: () => request<VendorStats>('/api/vendor/stats'),
    dashboard: () => request<VendorStats>('/api/vendor/dashboard'),
  },

  // Vendor profile / storefront
  profile: {
    updateBanner: (banner_url: string) =>
      request<{ banner_url: string }>('/api/vendor/banner', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ banner_url }),
      }),
  },

  // Customer messages (conversations where the vendor participates)
  conversations: {
    list: () => request<ConversationListResponse>('/api/conversations'),
    messages: (id: number) =>
      request<{ messages: MessageItem[] }>(`/api/conversations/${id}/messages`),
    sendMessage: (id: number, content: string) =>
      request<MessageItem>(`/api/conversations/${id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      }),
  },
};

export interface ParticipantInfo {
  user_id: number;
  name: string;
  email: string;
  avatar_url: string;
}

export interface ConversationItem {
  id: number;
  subject: string;
  product_id: number | null;
  last_message: string | null;
  last_message_at: string | null;
  unread_count: number;
  participants: ParticipantInfo[];
  created_at: string;
}

export interface ConversationListResponse {
  conversations: ConversationItem[];
}

export interface MessageItem {
  id: number;
  conversation_id: number;
  sender_id: number;
  content: string;
  created_at: string;
}

export interface VendorAttribute {
  id: number;
  name: string;
  value: string;
}

export interface VendorLicense {
  id: number;
  name: string;
  price: number;
  description: string | null;
}

export interface VendorVersion {
  id: number;
  version_name: string | null;
  ue_version: string;
  price: number | null;
  dev_compatibility: boolean;
  runtime_compatibility: boolean;
  changelog: string | null;
  file_size: number | null;
  storage_path: string | null;
  download_url: string | null;
}

export interface VendorAsset {
  id: number;
  name: string;
  description: string;
  price: number;
  vendor_id: number;
  category: string;
  thumbnail_url: string | null;
  is_featured: boolean;
  images: string[] | null;
  youtube_url: string | null;
  status: 'pending' | 'approved' | 'hidden' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  attributes: VendorAttribute[];
  versions: VendorVersion[];
  licenses: VendorLicense[];
}

export interface AssetPayload {
  name: string;
  description: string;
  price: number;
  category: string;
  thumbnail_url?: string | null;
  images?: string[] | null;
  youtube_url?: string | null;
  attributes?: VendorAttribute[];
  versions?: VersionPayload[];
  licenses?: LicensePayload[];
  storage_path?: string | null;
  download_url?: string | null;
}

export interface LicensePayload {
  name: string;
  price: number;
  description?: string | null;
}

export interface VersionPayload {
  version_name?: string | null;
  ue_version: string;
  price?: number | null;
  dev_compatibility?: boolean;
  runtime_compatibility?: boolean;
  changelog?: string | null;
  file_size?: number | null;
  storage_path?: string | null;
  download_url?: string | null;
}

export interface UploadResult {
  filename: string;
  storage_path: string;
  url: string;
  upload_url?: string | null;
}

export interface SalesItem {
  id: number;
  asset_id: number;
  asset_name: string;
  buyer_id: number;
  buyer_name: string;
  price: number;
  quantity: number;
  status: string;
  purchased_at: string;
  discount_amount: number;
  voucher_code: string | null;
}

export interface SalesResponse {
  sales: SalesItem[];
  total: number;
  total_revenue: number;
}

export interface CommissionEntry {
  asset_name: string;
  sales_count: number;
  commission_rate: number;
  total_earned: number;
}

export interface PayoutEntry {
  id: number;
  vendor_id: number;
  total_amount: number;
  status: string;
  method: string;
  date: string;
}

export interface VendorStats {
  total_earnings: number;
  total_assets: number;
  pending_approvals: number;
  total_sales: number;
  monthly_commissions: CommissionEntry[];
  payouts: PayoutEntry[];
}

export interface VendorPayout {
  id: number;
  vendor_id: number;
  amount: number;
  status: string;
  method: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export type { ApiError };
